<template>
  <div
    ref="card"
    class="card-compra-directa shadow-lg p-5 rounded-4 mx-auto text-center"
    style="max-width:480px;"
  >
    <h3 class="titulo-compra fw-bold mb-2">{{ $t('pronostico.directPurchaseTitle') }}</h3>
    <p class="subtitulo-compra mb-4">{{ $t('pronostico.directPurchaseSubtitle') }}</p>

    <!-- Correo -->
    <div class="mb-4 text-start">
      <label class="form-label fw-semibold">{{ $t('pronostico.email') }}</label>
      <input
        type="email"
        v-model.trim="correoCompra"
        class="form-control"
        :placeholder="$t('pronostico.emailPlaceholder')"
        required
        :disabled="bloquearUI"
      />
      <small class="text-muted d-block mb-2">{{ $t('pronostico.emailHelp') }}</small>
    </div>

    <!-- País -->
    <div class="mb-4 text-start qs-pais-compra">
      <label class="form-label fw-semibold">{{ $t('pronostico.country') }} <span class="text-danger">*</span></label>
      <select
        v-model="paisCompra"
        class="form-control"
        :class="{ 'is-invalid': correoValido && !paisValido && !bloquearUI }"
        :disabled="bloquearUI"
      >
        <option value="" disabled>{{ $t('pronostico.countryPlaceholder') }}</option>
        <option v-for="p in paisesOrdenados" :key="p" :value="p">{{ getCountryLabel(p) }}</option>
      </select>
      <small v-if="correoValido && !paisValido && !bloquearUI" class="text-danger d-block mt-1" style="font-size:0.78rem;">
        {{ $t('pronostico.countryRequired') }}
      </small>
      <small v-else class="text-muted d-block mb-2">{{ $t('pronostico.countryHelp') }}</small>
    </div>

    <!-- Consentimiento legal -->
    <label class="legal-check text-start mb-4">
      <input type="checkbox" v-model="aceptaEmails" :disabled="bloquearUI" />
      <span v-html="textoLegal"></span>
    </label>

    <!-- Botón generar pedido -->
    <QBtn
      type="button"
      variant="tipster"
      :label="$t('pronostico.continuePayment')"
      :disabled="!botonHabilitado || bloquearUI || !!clientSecret"
      :loading="bloquearUI && !clientSecret"
      :block="true"
      class="mt-3"
      @click.prevent="onContinuar"
    />

    <!-- Anchor de scroll: siempre en el DOM -->
    <div id="stripe-scroll-anchor" ref="stripeAnchor"></div>

    <!-- Stripe -->
    <div v-if="clientSecret && publicKey && botonHabilitado" id="stripe-form-container" ref="stripeContainer" class="stripe-container mx-auto mt-4 p-4 rounded-3">
      <!-- Precio -->
      <div v-if="!pagoCompletado" class="precio-pago text-center mb-4" style="color:#28a745; font-weight:600;">
        <label class="form-label fw-bold" style="font-size:1.15rem; color:#28a745;">
          {{ $t('pronostico.finalPrice') }}
        </label>

        <div v-if="descuentoOK">
          <div style="font-size:1rem; color:#666; text-decoration: line-through;">
            {{ Number(precioNormalMostrado).toFixed(2) }} {{ simboloMoneda }}
          </div>
          <div style="font-size:1.6rem; color:#28a745; margin-top:4px;">
            {{ Number(precioFinalMostrado).toFixed(2) }} {{ simboloMoneda }}
          </div>
        </div>

        <div v-else>
          <div v-if="producto?.rebaja_aplica">
            <div style="font-size:1rem; color:#666; text-decoration: line-through;">
              {{ Number(precioNormalMostrado).toFixed(2) }} {{ simboloMoneda }}
            </div>
            <div style="font-size:1.6rem; color:#28a745; margin-top:4px;">
              {{ Number((producto?.precio_mostrar || producto?.precio_mostrado || producto?.precio || 0) * rateMoneda).toFixed(2) }} {{ simboloMoneda }}
            </div>
          </div>
          <div v-else style="font-size:1.6rem; color:#28a745;">
            {{ Number(precioNormalMostrado).toFixed(2) }} {{ simboloMoneda }}
          </div>
        </div>
      </div>

      <component
        :is="usePaymentElement ? 'PaymentStripeElement' : 'PaymentStripe'"
        :key="clientSecret + '-' + idiomaActual"
        ref="stripeComponent"
        :clientSecret="clientSecret"
        :publicKey="publicKey"
        :email="correoCompra"
        :locale="idiomaActual"
        @cerrar-componente="reenviarCerrar"
      />
      <p v-if="bloquearUI && !pagoCompletado" class="text-muted mt-3" style="text-align:center;">
        {{ $t('pronostico.processingPayment') }}
      </p>
      <QBtn type="button" variant="tipster" :label="$t('pronostico.back')" :block="true" class="mt-4" @click.prevent="onCancelar" />
    </div>
  </div>
</template>

<script>
import PaymentStripe from "@/components/PaymentStripe.vue";
import PaymentStripeElement from "@/components/PaymentStripeElement.vue";

export default {
  name: "PronosticoCompraDirecta",
  components: { PaymentStripe, PaymentStripeElement },
  props: {
    clientSecret: { type: String, default: "" },
    publicKey: { type: String, default: "" },
    bloquearUI: { type: Boolean, default: false },
    pagoCompletado: { type: Boolean, default: false },
    descuentoOK: { type: Boolean, default: false },
    precioNormalMostrado: { type: Number, default: 0 },
    precioFinalMostrado: { type: Number, default: 0 },
    rateMoneda: { type: Number, default: 1 },
    simboloMoneda: { type: String, default: "€" },
    idiomaActual: { type: String, default: "es" },
    producto: { type: Object, default: () => ({}) },
    paises: { type: Array, default: () => [] },
    usePaymentElement: { type: Boolean, default: false },
  },
  emits: ["solicitar-pago", "cancelar", "cerrar-componente"],
  data() {
    return {
      correoCompra: "",
      paisCompra: "",
      aceptaEmails: false,
    };
  },
  computed: {
    correoValido() { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.correoCompra); },
    paisValido() { return !!this.paisCompra && this.paisCompra !== "0"; },
    botonHabilitado() { return this.correoValido && this.paisValido && this.aceptaEmails; },
    paisesOrdenados() {
      return [...this.paises].sort((a, b) =>
        this.getCountryLabel(a).localeCompare(this.getCountryLabel(b))
      );
    },
    textoLegal() {
      const aviso = `<a href="/aviso-legal" target="_blank" rel="noopener" class="legal-link">${this.$t('pronostico.registroInlineAvisoLegal')}</a>`;
      const privacidad = `<a href="/politica-privacidad" target="_blank" rel="noopener" class="legal-link">${this.$t('pronostico.registroInlinePrivacidad')}</a>`;
      return this.$t('pronostico.acceptTerms', { aviso, privacidad });
    },
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        try { this.$refs.card?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* noop */ }
      }, 200);
    });
  },
  methods: {
    reenviarCerrar(...args) {
      this.$emit('cerrar-componente', ...args);
    },
    onContinuar() {
      this.$emit("solicitar-pago", { correo: this.correoCompra, pais: this.paisCompra });
    },
    scrollToStripe() {
      // stripeAnchor siempre está en el DOM (fuera del v-if), scroll fiable
      const el = this.$refs.stripeAnchor;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    },
    onCancelar() {
      this.correoCompra = "";
      this.paisCompra = "";
      this.aceptaEmails = false;
      if (this.$refs.stripeComponent?.resetElements) {
        this.$refs.stripeComponent.resetElements();
      }
      this.$emit("cancelar");
    },
    getCountryLabel(code) {
      try {
        const value = this.$t(`countries.${code}`);
        if (value && value !== `countries.${code}`) return value;
      } catch (_) { /* noop */ }
      try {
        const names = new Intl.DisplayNames([this.$i18n.locale, 'es'], { type: 'region' });
        return names.of(code) || code;
      } catch (_) { return code; }
    },
  },
};
</script>

<style scoped>
#stripe-scroll-anchor {
  scroll-margin-top: 70px;
}

.legal-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.82rem;
  color: var(--q-muted);
  cursor: pointer;
  line-height: 1.45;
}
.legal-check input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--q-gold);
  width: 15px;
  height: 15px;
}
</style>
<style>
.legal-link {
  color: var(--q-gold);
  font-weight: 600;
}
.legal-link:hover { text-decoration: underline; }

@media (max-width: 600px) {
  .card-compra-directa {
    padding: 1.25rem 1rem !important;
  }
  .stripe-container {
    padding: 0.75rem 0.5rem !important;
  }
}
</style>
